import React, { useState, useEffect } from "react";
import { db, auth } from "../firebase";
import { collection, doc, getDoc, getDocs, query, where, orderBy, onSnapshot } from "firebase/firestore";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { updateDoc, increment } from "firebase/firestore"; // Import Firestore functions

function MyNetworkPage() {
  const [followingUsers, setFollowingUsers] = useState([]);
  const [expandedComments, setExpandedComments] = useState({});
  const [selectedUser, setSelectedUser] = useState(null);
  const [userPosts, setUserPosts] = useState([]);
  const [posts, setPosts] = useState([]);
  const [unreadPosts, setUnreadPosts] = useState({});
  const [likedPosts, setLikedPosts] = useState({});
  const [replyVisibility, setReplyVisibility] = useState({});
  const [replyText, setReplyText] = useState({});
  const [expandedPost, setExpandedPost] = useState(null);
  const [isPortrait, setIsPortrait] = useState(window.matchMedia("(orientation: portrait)").matches);



const toggleReplyBox = (commentId) => {
    setReplyVisibility(prev => ({
        ...prev,
        [commentId]: !prev[commentId] // Toggles visibility per comment
    }));
};


const markPostAsRead = (postId) => {
    setUnreadPosts(prev => {
        const updatedUnread = { ...prev };
        if (updatedUnread[postId]) {
            delete updatedUnread[postId]; // ✅ Remove unread status
        }
        return updatedUnread;
    });
};


	const toggleExpandedComments = (postId) => {
  setExpandedComments(prev => ({
    ...prev,
    [postId]: !prev[postId]
  }));
};



const toggleLike = async (postId) => {
    if (!auth.currentUser) return; // Ensure user is logged in

    const updatedLikes = { ...likedPosts, [postId]: !likedPosts[postId] };
    setLikedPosts(updatedLikes);

    const postRef = doc(db, "reviews", postId);

    try {
        await updateDoc(postRef, {
            likes: updatedLikes[postId] ? increment(1) : increment(-1) // ✅ Updates likes count in Firestore
        });
    } catch (error) {
        console.error("Error updating likes:", error);
    }
};
  

const { currentUser } = useAuth();
const navigate = useNavigate();

// Redirect if not logged in
useEffect(() => {
  if (!currentUser) {
    navigate("/login");
  }
}, [currentUser, navigate]);


  useEffect(() => {
  const handleResize = () => {
    setIsPortrait(window.matchMedia("(orientation: portrait)").matches);
  };
  window.addEventListener("resize", handleResize);
  return () => window.removeEventListener("resize", handleResize);
}, []);


  useEffect(() => {
    if (!auth.currentUser) return;
    const fetchFollowingUsers = async () => {
      const userRef = doc(db, "users", auth.currentUser.uid);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists() && userSnap.data().following) {
        setFollowingUsers(userSnap.data().following);
        if (userSnap.data().following.length > 0) {
          setSelectedUser(userSnap.data().following[0]); // Auto-select first user
        }
      }
    };
    fetchFollowingUsers();
  }, []);

  useEffect(() => {
  if (!selectedUser) return;

  const postsQuery = query(
    collection(db, "reviews"),
    where("userId", "==", selectedUser.userId),
    orderBy("createdAt", "desc")
  );

  // Set up a Firestore listener for real-time updates
  const unsubscribe = onSnapshot(postsQuery, (querySnapshot) => {
    const updatedPosts = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    setUserPosts(updatedPosts);
  });

  return () => unsubscribe(); // Cleanup listener on unmount
}, [selectedUser]);



 useEffect(() => {
    if (!auth.currentUser) return;

    const fetchUnreadPosts = async () => {
        const userRef = doc(db, "users", auth.currentUser.uid);
        const userSnap = await getDoc(userRef);
        const lastViewed = userSnap.exists() ? userSnap.data().lastViewed || {} : {};

        const unsubscribe = onSnapshot(collection(db, "reviews"), (snapshot) => {
            let newUnread = {};
            snapshot.forEach(doc => {
                const postData = doc.data();
                if (followingUsers.some(user => user.userId === postData.userId) && postData.createdAt) {
                    const lastViewedTime = lastViewed[postData.userId] || "2000-01-01T00:00:00Z";
                    if (new Date(postData.createdAt.toDate()) > new Date(lastViewedTime)) {
                        newUnread[postData.userId] = true;  // ✅ Mark unread
                    }
                }
            });
            setUnreadPosts(newUnread);
        });

        return () => unsubscribe();
    };

    fetchUnreadPosts();
}, [followingUsers]);


const handleUserSelection = async (user) => {
    setSelectedUser(user); // ✅ Select the user to load their posts

    if (!auth.currentUser) return;

    const userRef = doc(db, "users", auth.currentUser.uid);
    try {
        // ✅ Update last viewed timestamp for this user in Firestore
        await updateDoc(userRef, {
            [`lastViewed.${user.userId}`]: new Date().toISOString()
        });

        // ✅ Update unreadPosts state (remove the dot)
        setUnreadPosts(prevUnread => {
            const updatedUnread = { ...prevUnread };
            delete updatedUnread[user.userId]; // ✅ Remove unread indicator
            return updatedUnread;
        });
    } catch (error) {
        console.error("Error updating last viewed timestamp:", error);
    }
};


const handleSubmitReply = async (postId, parentCommentId = null) => {
    if (!auth.currentUser) {
        console.error("User not authenticated");
        return;
    }

    const postRef = doc(db, "reviews", postId);
    const postSnap = await getDoc(postRef);

    if (!postSnap.exists()) return;

    const existingResponses = postSnap.data().responses || [];

    // Fetch the correct username from Firestore
    const userRef = doc(db, "users", auth.currentUser.uid);
    const userSnap = await getDoc(userRef);
    const username = userSnap.exists() ? userSnap.data().username : "Anonymous";

    const newReply = {
        id: Date.now().toString(),
        username: username,
        text: replyText[parentCommentId || postId] || "",
        createdAt: new Date().toISOString(),
        replies: []
    };

    let updatedResponses;

    if (parentCommentId) {
        updatedResponses = existingResponses.map(comment =>
            comment.id === parentCommentId
                ? { ...comment, replies: [...(comment.replies || []), newReply] }
                : comment
        );
    } else {
        updatedResponses = [...existingResponses, newReply];
    }

    // Update Firestore
    await updateDoc(postRef, { responses: updatedResponses });

    // **Real-Time Update: Modify UI Immediately**
    setUserPosts(prevPosts =>
        prevPosts.map(post =>
            post.id === postId ? { ...post, responses: updatedResponses } : post
        )
    );

    // Clear input field after submission
    setReplyText(prev => ({ ...prev, [parentCommentId || postId]: "" }));

    setReplyVisibility(prev => ({ ...prev, [parentCommentId || postId]: false }));

};



  return (
  <div style={{ position: "relative", minHeight: "100vh", overflow: "hidden" }}>

    
     {/* ✅ Background Logo (behind content) */}
  {!isPortrait && (
  <div
    style={{
      position: "absolute",
      top: 0,
      bottom: 0,
      right: 0,
      left: 0,
      zIndex: 0,
      pointerEvents: "none"
    }}
  >
    <img
      src="/vibescan-logo.png"
      alt="VibeScan Logo"
      style={{
        position: "absolute",
        top: "-39px",
        right: "-4%",
        opacity: 0.45,
        width: "300px",
        maxWidth: "40vw"
      }}
    />
  </div>
  )}


    {/* ✅ Foreground Content */}
  <div style={{ position: "relative", zIndex: 1 }}>
    <div style={{ display: "flex", gap: "20px", padding: "20px" }}>
	
  
    <div style={{ display: "flex", gap: "20px", padding: "0px" }}>
      <div style={{ flex: "1", padding: "10px" }}>
        <h3>Following</h3>
        <ul style={{ listStyleType: "none", padding: 0 }}>
    {followingUsers.length === 0 ? (
  <p style={{ fontStyle: "italic", color: "#555" }}>
    You're not following anyone yet. When you follow users, their posts will appear here so you can keep up with their experiences.
  </p>
) : (
  followingUsers.map(user => (
    <li key={user.userId} style={{ padding: "5px", cursor: "pointer" }} onClick={() => handleUserSelection(user)}>
      {user.username} {unreadPosts[user.userId] && <span style={{ color: "yellow", marginLeft: "5px" }}>●</span>}
    </li>
  ))
)}

</ul>
      </div>
      <div
  style={{
    flex: 3,
    padding: "10px",
    minWidth: "0", // ✅ Important for flex shrink
    maxWidth: "100%", // ✅ Prevents overflow on small screens
    boxSizing: "border-box", // ✅ Includes padding in width calculation
    overflowWrap: "break-word", // ✅ Forces long words to wrap
  }}
>

        {selectedUser && (
          <>
            <h3>Posts from {selectedUser.username}</h3>
            {userPosts.length === 0 ? (
              <p>No posts available.</p>
            ) : (
              <ul style={{ listStyleType: "none", padding: 0 }}>
                {userPosts.slice(0, 20).map(post => (
                  <li key={post.id} 
    style={{ padding: "10px", borderBottom: "1px solid #ddd", cursor: "pointer" }}
    onClick={() => markPostAsRead(post.id)}>

                    <h4>{post.company} ({post.city}, {post.state})</h4>
                    <p><strong>Job Title:</strong> {post.position || "N/A"}</p>
                    <p style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
  {expandedPost === post.id
    ? post.comments
    : `${post.comments ? post.comments.substring(0, 320) : ""}...`}
  {post.comments && post.comments.length > 320 && (
    <button
      onClick={() =>
        setExpandedPost(expandedPost === post.id ? null : post.id)
      }
      style={{
        border: "none",
        background: "none",
        color: "blue",
        cursor: "pointer",
        marginLeft: "5px"
      }}
    >
      {expandedPost === post.id ? "Show Less" : "Show More"}
    </button>
  )}
</p>

		<button
  className={`like-button ${likedPosts[post.id] ? "liked" : ""}`}
  onClick={() => toggleLike(post.id)}
  style={{
    border: "none",
    background: "none",
    color: likedPosts[post.id] ? "red" : "#007bff",
    fontSize: "14px",
    cursor: "pointer",
    marginRight: "1px", // Adds space to the right
  }}
>
  ❤
</button>
<span style={{ fontSize: "14px", color: "#666", marginRight: "16px"}}>
  {posts.find((p) => p.id === post.id)?.likes || 0}
</span>

<button
  onClick={() => toggleReplyBox(post.id)}
  className="modern-button"
  style={{
    border: "none",
    background: "none",
    color: "#007bff",
    fontSize: "14px",
    cursor: "pointer",
    padding: "8px 12px",
    borderRadius: "4px",
    
  }}
>
  {replyVisibility[post.id] ? "Cancel" : "Comment"}
</button>




{replyVisibility[post.id] && (
  <div>
    <textarea
      placeholder="Write a reply..."
      value={replyText[post.id] || ""}
      onChange={(e) =>
        setReplyText((prev) => ({ ...prev, [post.id]: e.target.value }))
      }
      style={{ width: "100%", marginTop: "10px" }}
    />
    <button
      onClick={() => handleSubmitReply(post.id)}
      className="modern-button"
      disabled={!replyText[post.id]?.trim()}
    >
      Send
    </button>
  </div>
)}


{/* Display Comments and Nested Replies */}
{post.responses && post.responses.length > 0 && (
  <div style={{ marginTop: "10px", paddingLeft: "15px", borderLeft: "2px solid #ddd" }}>
    {(expandedComments[post.id] 
  ? post.responses 
  : post.responses.slice(0, 1)
).map((response, index) => (

      <div key={index} style={{ marginBottom: "8px" }}>
        <p>
          <strong>{response.username || "Anonymous"}:</strong> {response.text}
        </p>

        {/* ✅ Nested Replies */}
        {response.replies && response.replies.length > 0 && (
          <div style={{ marginLeft: "20px", paddingLeft: "10px", borderLeft: "1px solid #ccc" }}>
            {response.replies.map((reply, replyIndex) => (
              <p key={replyIndex} style={{ fontSize: "14px" }}>
                <strong>{reply.username || "Anonymous"}:</strong> {reply.text}
              </p>
            ))}
          </div>
        )}

        {/* ✅ Reply Button */}
        <button className="reply-button" onClick={() => toggleReplyBox(response.id)}>
          Reply
        </button>


	{/* ✅ See More Comments Button */}
	{post.responses.length > 1 && (
  <button
    onClick={() => toggleExpandedComments(post.id)}
    style={{
      marginTop: "5px",
      background: "none",
      border: "none",
      color: "#007bff",
      cursor: "pointer",
      fontSize: "14px"
    }}
  >
   {expandedComments[post.id] 
      ? "Hide comments" 
      : `See more comments (${post.responses.length - 1 + post.responses.slice(1).reduce((acc, r) => acc + (r.replies?.length || 0), 0)})`}
  </button>
)}




        {/* ✅ Reply Input Box */}
        {replyVisibility[response.id] && (
          <div>
            <textarea
              placeholder="Write a reply..."
              value={replyText[response.id] || ""}
              onChange={(e) => setReplyText(prev => ({ ...prev, [response.id]: e.target.value }))}
              style={{ width: "100%", marginTop: "5px" }}
            />
            <button className="modern-button" onClick={() => handleSubmitReply(post.id, response.id)}>
              Send
            </button>
          </div>
        )}
      </div>
    ))}
  </div>
)}

                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </div>
	</div>
	</div>
	</div>
  );
}

export default MyNetworkPage;

